import { ContentService } from '@blk/cwp-ng-framework';
import { BfaConstants } from '@ddt-web/domain';
import { NumberUtil } from '@ddt-web/generic-utils';

import { Formatter } from './formatter.interface';

export class NumberFormatter implements Formatter {
	public static readonly PERCENT: string = '%';
	public static readonly YEAR: string = 'yr';
	public static readonly NONE: string = '';
	public static readonly NOT_APPLICABLE: string = 'n/a';
	public static readonly DASH_VALUE: string = '-';
	public static readonly DOLLAR: string = '$';

	public static create(options: Partial<NumberFormatterOptions>): NumberFormatter {
		return new this(
			options.suffix,
			options.decimal,
			options.defaultNullValue,
			options.defaultUndefinedValue,
			options.prefix,
			options.multiplier,
			options.showPlusSign,
			options.defaultZeroValue,
			options.useAccountingFormatForNegative,
			options.defaultNaNValue,
			options.hideMinusNegativeValue
		);
	}

	constructor(
		public readonly suffix: string = NumberFormatter.NONE,
		public readonly decimal: number = BfaConstants.DEFAULT_DECIMAL_PLACES,
		public readonly defaultNullValue = NumberFormatter.NOT_APPLICABLE,
		public readonly defaultUndefinedValue = NumberFormatter.DASH_VALUE,
		public readonly prefix: string = NumberFormatter.NONE,
		public readonly multiplier?: number,
		public readonly showPlusSign: boolean = false,
		public readonly defaultZeroValue?: string,
		public readonly useAccountingFormatForNegative: boolean = false,
		public readonly defaultNaNValue = NumberFormatter.DASH_VALUE,
		public readonly hideMinusNegativeValue: boolean = false
	) { }

	public format(value: any, contentSvc?: ContentService): string {
		if (value === null) {
			return this.defaultNullValue;
		}

		if (value === undefined) {
			return this.defaultUndefinedValue;
		}

		if (isNaN(value)) {
			return this.defaultNaNValue;
		}

		if (typeof value === 'string' && !isNaN(Number(value))) {
			if (value === '') {
				return this.defaultUndefinedValue || NumberFormatter.DASH_VALUE;
			}
			value = Number(value);
		}

		if (typeof value === 'number' && this.multiplier) {
			value = value * this.multiplier;
		}

		if (typeof value === 'number') {
			if (value === 0 && this.defaultZeroValue) {
				return this.defaultZeroValue;
			} else {
				if (this.useAccountingFormatForNegative) {
					return this.prefix
						+ (value < 0 ? '(' : '')
						+ NumberUtil.formatAndConvertNegativeZero(value, this.decimal).replace('-', '')
						+ (value < 0 ? ')' : '')
						+ this.suffix;
				} else if (this.hideMinusNegativeValue) {
					return this.prefix
						+ NumberUtil.formatAndConvertNegativeZero(value, this.decimal).replace('-', '')
						+ this.suffix;
				} else {
					return this.prefix
						+ (this.showPlusSign && value > 0 ? '+' : '')
						+ NumberUtil.formatAndConvertNegativeZero(value, this.decimal)
						+ this.suffix;
				}

			}
		} else {
			return value;
		}
	}
}

export interface NumberFormatterOptions {
	suffix: string;
	decimal: number;
	defaultNullValue: string;
	defaultUndefinedValue: string;
	prefix: string;
	multiplier: number;
	showPlusSign: boolean;
	defaultZeroValue: string;
	useAccountingFormatForNegative: boolean;
	defaultNaNValue: string;
	hideMinusNegativeValue: boolean;
}
